import { useState, useRef } from 'react';
import {
  Package,
  MapPin,
  Phone,
  User,
  Truck,
  CheckCircle,
  RefreshCw,
  Search,
  Eye,
  X,
  Calendar,
  Box,
  Camera,
  Upload,
  Loader2
} from 'lucide-react';
import { useEnvios } from '../../hooks/useEnvios';
import { useAuthStore } from '../../store/authStore';
import { Badge } from '../../components/ui/Badge';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../api/clients';
import { CameraModal } from '../../components/ui/CameraModal';

type TabType = 'mis-envios' | 'disponibles';

const getEstadoBadge = (estado: string) => {
  const variants: Record<string, { variant: 'warning' | 'info' | 'success' | 'danger' | 'default'; label: string }> = {
    pendiente: { variant: 'warning', label: 'Pendiente' },
    asignado: { variant: 'info', label: 'Asignado' },
    en_camino: { variant: 'info', label: 'En Camino' },
    en_proceso: { variant: 'info', label: 'En Proceso' },
    entregado: { variant: 'success', label: 'Entregado' },
    cancelado: { variant: 'danger', label: 'Cancelado' },
  };
  const config = variants[estado] || variants.pendiente;
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

export function MisEnviosRepartidor() {
  const [activeTab, setActiveTab] = useState<TabType>('mis-envios');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEnvio, setSelectedEnvio] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [observaciones, setObservaciones] = useState('');
  const [imagenUrl, setImagenUrl] = useState<string | null>(null);
  const [loadingAction, setLoadingAction] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { user } = useAuthStore();
  const { data: allEnvios, isLoading, refetch } = useEnvios();

  const misEnvios = allEnvios?.filter((e: any) => e.repartidorId === user?.id) || [];
  const enviosDisponibles = allEnvios?.filter((e: any) => e.estado === 'pendiente') || [];

  const getEnvios = () => activeTab === 'mis-envios' ? misEnvios : enviosDisponibles;
  const envios = getEnvios();

  const filteredEnvios = envios.filter((envio: any) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      envio.numeroEnvio?.toLowerCase().includes(searchLower) ||
      envio.clienteNombre?.toLowerCase().includes(searchLower) ||
      envio.clienteDireccion?.toLowerCase().includes(searchLower)
    );
  });

  const handleTomarEnvio = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres tomar este envío?')) return;
    setLoadingAction(true);
    try {
      await api.post(`/logistica/envios/${id}/tomar`);
      toast.success('Envío tomado exitosamente');
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al tomar el envío');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleMarcarEnCamino = async (id: number) => {
    setLoadingAction(true);
    try {
      await api.put(`/logistica/envios/${id}/en-camino`);
      toast.success('Envío marcado como en camino');
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al actualizar el envío');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleCameraCapture = async (imageData: string) => {
    setIsUploadingImage(true);
    try {
      const response = await fetch(imageData);
      const blob = await response.blob();
      const file = new File([blob], 'entrega-evidence.jpg', { type: 'image/jpeg' });
      
      const formData = new FormData();
      formData.append('files', file);

      const uploadResponse = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const data = uploadResponse.data.data;
      if (data.imagenes && data.imagenes.length > 0) {
        setImagenUrl(data.imagenes[0]);
        toast.success('Foto capturada correctamente');
      }
    } catch (error) {
      toast.error('Error al subir la imagen');
    } finally {
      setIsUploadingImage(false);
      setShowCameraModal(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar los 5MB');
      return;
    }

    setIsUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('files', file);

      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const data = response.data.data;
      if (data.imagenes && data.imagenes.length > 0) {
        setImagenUrl(data.imagenes[0]);
        toast.success('Imagen subida correctamente');
      }
    } catch (error) {
      toast.error('Error al subir la imagen');
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleMarcarEntregado = async () => {
    if (!selectedEnvio) return;
    setLoadingAction(true);
    try {
      const payload: any = { observaciones };
      if (imagenUrl) {
        payload.imagenObservacionUrl = imagenUrl;
      }

      await api.put(`/logistica/envios/${selectedEnvio.id}/entregado`, payload);
      toast.success('Envío marcado como entregado');
      setShowConfirmModal(false);
      setSelectedEnvio(null);
      setObservaciones('');
      setImagenUrl(null);
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al entregar el envío');
    } finally {
      setLoadingAction(false);
    }
  };

  const openDetail = (envio: any) => {
    setSelectedEnvio(envio);
    setShowModal(true);
  };

  const parseProductos = (productos: any) => {
    if (!productos) return [];
    if (typeof productos === 'string') {
      try { return JSON.parse(productos); } catch { return []; }
    }
    return Array.isArray(productos) ? productos : [];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando envíos...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800"> Envíos</h1>
          <p className="text-sm text-gray-500">{filteredEnvios.length} envío(s) encontrados</p>
        </div>
        <div className="text-sm text-gray-400">
          {new Date().toLocaleDateString('es-AR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-gray-200 mb-4">
        <button
          onClick={() => setActiveTab('mis-envios')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'mis-envios'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Package size={16} className="inline mr-2" />
          Mis Envíos ({misEnvios.length})
        </button>
        <button
          onClick={() => setActiveTab('disponibles')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'disponibles'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <RefreshCw size={16} className="inline mr-2" />
          Envíos Disponibles ({enviosDisponibles.length})
        </button>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <input
          type="text"
          placeholder="Buscar por número, cliente o dirección..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredEnvios.map((envio: any) => {
          const productos = parseProductos(envio.productos);
          
          return (
            <div
              key={envio.id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow cursor-pointer group"
              onClick={() => openDetail(envio)}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Package size={16} className="text-blue-500" />
                    <h3 className="font-semibold text-gray-800">{envio.numeroEnvio}</h3>
                    {getEstadoBadge(envio.estado)}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {envio.fechaAsignacion ? `Asignado: ${formatDate(envio.fechaAsignacion)}` : `Creado: ${formatDate(envio.fechaCreacion)}`}
                  </p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); openDetail(envio); }}
                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                >
                  <Eye size={18} />
                </button>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <User size={14} className="text-gray-400 shrink-0 mt-0.5" />
                  <span className="text-gray-700 font-medium">{envio.clienteNombre}</span>
                </div>
                {envio.clienteTelefono && (
                  <div className="flex items-start gap-2">
                    <Phone size={14} className="text-gray-400 shrink-0 mt-0.5" />
                    <a
                      href={`tel:${envio.clienteTelefono.replace(/\s/g, '')}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline transition-colors text-sm"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {envio.clienteTelefono}
                    </a>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <MapPin size={14} className="text-gray-400 shrink-0 mt-0.5" />
                  <a
                    href={`https://www.google.com/maps/search/${encodeURIComponent(envio.clienteDireccion)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline transition-colors text-sm line-clamp-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {envio.clienteDireccion}
                  </a>
                </div>
              </div>

              <div className="mt-3 p-2 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 font-medium"> Contenido:</p>
                <p className="text-sm text-gray-700 line-clamp-2">
                  {envio.paqueteContenido || 
                    (productos.length > 0 ? productos.map((p: any) => p.nombre || p.productoNombre).join(', ') : 'Sin descripción')}
                </p>
              </div>

              <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2">
                {activeTab === 'disponibles' && envio.estado === 'pendiente' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleTomarEnvio(envio.id); }}
                    disabled={loadingAction}
                    className="flex-1 flex items-center justify-center gap-1 text-sm text-blue-600 border border-blue-300 hover:bg-blue-50 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Truck size={14} /> Tomar Envío
                  </button>
                )}
                {activeTab === 'mis-envios' && envio.estado === 'asignado' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleMarcarEnCamino(envio.id); }}
                    disabled={loadingAction}
                    className="flex-1 flex items-center justify-center gap-1 text-sm text-blue-600 border border-blue-300 hover:bg-blue-50 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Truck size={14} /> Marcar En Camino
                  </button>
                )}
                {activeTab === 'mis-envios' && envio.estado === 'en_camino' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedEnvio(envio); setShowConfirmModal(true); }}
                    disabled={loadingAction}
                    className="flex-1 flex items-center justify-center gap-1 text-sm text-green-600 border border-green-300 hover:bg-green-50 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <CheckCircle size={14} /> Confirmar Entrega
                  </button>
                )}
                {activeTab === 'mis-envios' && envio.estado === 'entregado' && (
                  <span className="flex-1 flex items-center justify-center gap-1 text-sm text-green-600 bg-green-50 py-1.5 rounded-lg">
                    <CheckCircle size={14} /> Entregado
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredEnvios.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <Package size={48} className="mx-auto mb-4 text-gray-300" />
          <p>No hay envíos para mostrar</p>
          {activeTab === 'disponibles' && (
            <p className="text-sm mt-1">No hay envíos disponibles en este momento</p>
          )}
          {activeTab === 'mis-envios' && (
            <p className="text-sm mt-1">No tienes envíos asignados</p>
          )}
        </div>
      )}

      {/* Modal de Detalle */}
      {showModal && selectedEnvio && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <Package size={24} className="text-blue-500" />
                <div>
                  <h2 className="text-xl font-bold text-gray-800">{selectedEnvio.numeroEnvio}</h2>
                  <div className="flex items-center gap-2 mt-0.5">
                    {getEstadoBadge(selectedEnvio.estado)}
                    <span className="text-xs text-gray-400">{formatDate(selectedEnvio.fechaCreacion)}</span>
                  </div>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Datos del cliente*/}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <User size={16} /> Datos del Cliente
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Nombre</p>
                    <p className="font-medium text-gray-800">{selectedEnvio.clienteNombre}</p>
                  </div>
                  {selectedEnvio.clienteDocumento && (
                    <div>
                      <p className="text-gray-500">Documento</p>
                      <p className="font-medium text-gray-800">{selectedEnvio.clienteDocumento}</p>
                    </div>
                  )}
                  {selectedEnvio.clienteTelefono && (
                    <div>
                      <p className="text-gray-500">Teléfono</p>
                      <a
                        href={`tel:${selectedEnvio.clienteTelefono.replace(/\s/g, '')}`}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors flex items-center gap-2"
                      >
                        <Phone size={14} className="text-blue-500" />
                        {selectedEnvio.clienteTelefono}
                      </a>
                    </div>
                  )}
                  {selectedEnvio.clienteLocalidad && (
                    <div>
                      <p className="text-gray-500">Localidad</p>
                      <p className="font-medium text-gray-800">{selectedEnvio.clienteLocalidad}</p>
                    </div>
                  )}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-gray-500 text-sm">Dirección</p>
                  <a
                    href={`https://www.google.com/maps/search/${encodeURIComponent(selectedEnvio.clienteDireccion)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors flex items-start gap-2"
                  >
                    <MapPin size={16} className="text-blue-500 shrink-0 mt-0.5" />
                    {selectedEnvio.clienteDireccion}
                  </a>
                </div>
              </div>

              {/* Productos */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Box size={16} /> Productos
                </h3>
                {selectedEnvio.paqueteContenido && (
                  <div className="mb-3">
                    <p className="text-gray-500 text-sm">Contenido del paquete</p>
                    <p className="font-medium text-gray-800">{selectedEnvio.paqueteContenido}</p>
                  </div>
                )}
                {parseProductos(selectedEnvio.productos).length > 0 ? (
                  <ul className="space-y-1">
                    {parseProductos(selectedEnvio.productos).map((p: any, index: number) => (
                      <li key={index} className="flex items-center gap-2 text-sm">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full shrink-0"></span>
                        <span className="font-medium text-gray-800">
                          {p.nombre || p.productoNombre || 'Producto'}
                          {p.cantidad && p.cantidad > 1 && (
                            <span className="text-gray-500 text-xs ml-1">(x{p.cantidad})</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 text-sm">No hay productos detallados</p>
                )}
              </div>

              {/* Información adicional */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Calendar size={16} /> Información adicional
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-gray-500">Creado</span>
                    <span className="font-medium text-gray-800">{formatDate(selectedEnvio.fechaCreacion)}</span>
                  </div>
                  {selectedEnvio.fechaEnvio && (
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-gray-500">En camino</span>
                      <span className="font-medium text-gray-800">{formatDate(selectedEnvio.fechaEnvio)}</span>
                    </div>
                  )}
                  {selectedEnvio.fechaEntrega && (
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-gray-500">Entregado</span>
                      <span className="font-medium text-gray-800">{formatDate(selectedEnvio.fechaEntrega)}</span>
                    </div>
                  )}
                  {selectedEnvio.numeroSeguimiento && (
                    <div className="flex justify-between py-1">
                      <span className="text-gray-500">N° Seguimiento</span>
                      <span className="font-medium text-gray-800">{selectedEnvio.numeroSeguimiento}</span>
                    </div>
                  )}
                </div>
                {selectedEnvio.observaciones && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-gray-500 text-sm">Observaciones</p>
                    <p className="font-medium text-gray-800 text-sm">{selectedEnvio.observaciones}</p>
                  </div>
                )}
                {selectedEnvio.imagenObservacionUrl && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-gray-500 text-sm">Foto de evidencia</p>
                    <img
                      src={selectedEnvio.imagenObservacionUrl}
                      alt="Evidencia"
                      className="max-w-xs max-h-48 rounded-lg border border-gray-200 shadow-sm object-contain bg-gray-50 mt-1"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                {selectedEnvio.estado === 'asignado' && (
                  <button
                    onClick={() => { handleMarcarEnCamino(selectedEnvio.id); setShowModal(false); }}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg transition-colors"
                  >
                    <Truck size={18} /> Marcar En Camino
                  </button>
                )}
                {selectedEnvio.estado === 'en_camino' && (
                  <button
                    onClick={() => { setShowModal(false); setShowConfirmModal(true); }}
                    className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-lg transition-colors"
                  >
                    <CheckCircle size={18} /> Confirmar Entrega
                  </button>
                )}
                {selectedEnvio.estado === 'entregado' && (
                  <div className="flex-1 flex items-center justify-center gap-2 bg-green-50 text-green-700 py-2.5 rounded-lg">
                    <CheckCircle size={18} /> Entregado el {formatDate(selectedEnvio.fechaEntrega)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de entrega */}
      {showConfirmModal && selectedEnvio && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Confirmar Entrega</h3>
            <p className="text-sm text-gray-600 mb-4">
              Lo recibió: <strong>{selectedEnvio.clienteNombre}</strong>
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones (opcional)</label>
              <textarea
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Ej: Entregado en puerta trasera"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
              />
            </div>

            {/* Sección de foto de evidencia */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adjuntar foto de evidencia (opcional)
              </label>
              
              {imagenUrl ? (
                <div className="relative inline-block">
                  <img
                    src={imagenUrl}
                    alt="Evidencia"
                    className="max-w-full max-h-48 rounded-lg border border-gray-200 object-contain bg-gray-50"
                  />
                  <button
                    type="button"
                    onClick={() => setImagenUrl(null)}
                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setShowCameraModal(true)}
                    disabled={isUploadingImage}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {isUploadingImage ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Camera size={18} />
                    )}
                    {isUploadingImage ? 'Subiendo...' : 'Tomar foto'}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploadingImage}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {isUploadingImage ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Upload size={18} />
                    )}
                    {isUploadingImage ? 'Subiendo...' : 'Subir imagen'}
                  </button>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">
                Formatos: JPG, PNG, WEBP • Máximo 5MB
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleMarcarEntregado}
                disabled={loadingAction || isUploadingImage}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loadingAction ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <CheckCircle size={18} />
                )}
                Confirmar
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedEnvio(null);
                  setObservaciones('');
                  setImagenUrl(null);
                }}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 rounded-lg transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cámara */}
      <CameraModal
        isOpen={showCameraModal}
        onClose={() => setShowCameraModal(false)}
        onCapture={handleCameraCapture}
        title="Tomar foto de evidencia"
      />
    </div>
  );
}